/* Keep each heading with its specifications; enhance presentation only. */
(() => {
  'use strict';
  const roots = '.rhl-pdp-desc, .hdt-product__description > .hdt-rte';
  const text = node => (node.textContent || '').replace(/\s+/g, ' ').trim();
  const media = 'table, img, picture, video, iframe, form';
  const spacer = node => node.nodeType === 8 ||
    (node.nodeType === 3 && !text(node)) ||
    (node.nodeType === 1 && /^(P|DIV|BR)$/.test(node.tagName) && !text(node) && !node.querySelector(media));

  function isSpecHeading(node) {
    if (node.nodeType !== 1 || !/^(P|DIV|H[2-6]|STRONG|B)$/.test(node.tagName)) return false;
    const label = text(node);
    if (!label || label.length > 90 || node.querySelector('ul, ol, table, p, div, img, a')) return false;
    const named = /\b(dimensions?|measurements?|sizes?|specifications?|technical details|product details)\b/i.test(label);
    const sized = /^(?:(?:small|medium|large|extra large)\b|\d+(?:\.\d+)?\s*(?:["\u2033]|''|inches?\b|cm\b))/i.test(label);
    const bold = [...node.querySelectorAll('strong, b')].some(child => text(child) === label);
    return (named || sized) && (bold || /^(H[2-6]|STRONG|B)$/.test(node.tagName) || /[:\uff1a]\s*$/.test(label));
  }

  function nextContent(node) {
    let next = node.nextSibling;
    while (next && spacer(next)) next = next.nextSibling;
    return next;
  }

  function needsFullWidth(items) {
    return items.length > 12 || items.some(item => text(item).length > 120 || item.querySelector(media + ', ul, ol')) ||
      items.reduce((sum, item) => sum + text(item).length, 0) > 900;
  }

  // Some imports put several dimension headings inside one UL rather than
  // using separate headings and lists. Keep each heading with all its values.
  function isListHeading(item) {
    if (item.tagName !== 'LI' || item.querySelector(media + ', ul, ol, a')) return false;
    const label = text(item);
    if (!label || label.length > 90) return false;
    const clean = value => value.replace(/[:\uff1a]\s*$/, '').trim();
    const bold = [...item.querySelectorAll('strong, b')].some(node => clean(text(node)) === clean(label));
    const dimension = /\b(dimensions?|measurements?|sizes?|heights?|widths?|lengths?|depths?|diameters?|weights?|canopy|canopies|shades?|cables?|wires?|rods?|chains?|drops?)\b/i.test(label);
    const sized = /^(?:(?:small|medium|large|extra large)\b|\d+(?:\.\d+)?\s*(?:["\u2033]|''|inches?\b|cm\b))/i.test(label);
    return bold && (dimension || sized);
  }

  function splitListGroups(list) {
    // Ordered-list numbering and nested-list semantics stay untouched.
    if (list.tagName !== 'UL' || list.closest('.rhl-spec-grid') || !isSpecHeading(list.previousElementSibling || document.createTextNode(''))) return;
    const nodes = [...list.childNodes];
    const items = [...list.children];
    if (items.length < 4 || items.some(item => item.tagName !== 'LI') ||
        nodes.some(node => node.nodeType === 3 && text(node)) || !isListHeading(items[0])) return;
    const groups = [];
    nodes.forEach(node => {
      if (node.nodeType === 1 && isListHeading(node)) groups.push([]);
      if (groups.length) groups[groups.length - 1].push(node);
    });
    if (groups.length < 2 || groups.some(group => group.filter(node => node.nodeType === 1).length < 2)) return;
    const grid = document.createElement('div');
    grid.className = 'rhl-spec-grid rhl-spec-grid--list-groups';
    // Preserve leading whitespace/comments and the original list ID once.
    while (list.firstChild && list.firstChild !== items[0]) grid.appendChild(list.firstChild);
    groups.forEach((group, index) => {
      const section = document.createElement('div');
      const entries = group.filter(node => node.nodeType === 1);
      section.className = 'rhl-spec-group' + (needsFullWidth(entries.slice(1)) ? ' rhl-spec-group--wide' : '');
      const part = list.cloneNode(false);
      if (index) part.removeAttribute('id');
      entries[0].classList.add('rhl-spec-heading');
      group.forEach(node => part.appendChild(node));
      section.appendChild(part);
      grid.appendChild(section);
    });
    list.replaceWith(grid);
  }

  function specGroup(heading) {
    if (!isSpecHeading(heading)) return null;
    let body = nextContent(heading);
    if (!body || body.nodeType !== 1 || !/^(UL|OL)$/.test(body.tagName)) return null;
    const lists = [body];
    let next = nextContent(body);
    while (next && next.nodeType === 1 && /^(UL|OL)$/.test(next.tagName)) {
      lists.push(next);
      body = next;
      next = nextContent(next);
    }
    const items = lists.flatMap(list => [...list.children]);
    // Long explanations, nested lists and embedded media retain the full line.
    const wide = needsFullWidth(items);
    return { heading, last: body, next, wide };
  }

  function enhance(container) {
    // Imported descriptions sometimes place the whole body inside a wrapper.
    [...container.children].forEach(child => {
      if (child.matches('div, section, article') && !child.matches('.rhl-spec-grid, .rhl-spec-group')) enhance(child);
    });
    [...container.children].filter(child => child.tagName === 'UL').forEach(splitListGroups);
    let node = container.firstChild;
    while (node) {
      const first = specGroup(node);
      if (!first) { node = node.nextSibling; continue; }
      const run = [first];
      let next = first.next;
      let group;
      while ((group = next && specGroup(next))) { run.push(group); next = group.next; }
      if (run.length < 2) { node = next; continue; }
      const grid = document.createElement('div');
      grid.className = 'rhl-spec-grid';
      container.insertBefore(grid, first.heading);
      run.forEach(entry => {
        const section = document.createElement('div');
        section.className = 'rhl-spec-group' + (entry.wide ? ' rhl-spec-group--wide' : '');
        entry.heading.classList.add('rhl-spec-heading');
        let current = entry.heading;
        while (current) {
          const following = current.nextSibling;
          section.appendChild(current);
          if (current === entry.last) break;
          current = following;
        }
        grid.appendChild(section);
        // Preserve inter-group whitespace without creating empty grid items.
        while (grid.nextSibling && spacer(grid.nextSibling)) section.appendChild(grid.nextSibling);
      });
      node = next;
    }
  }

  function init(scope = document) {
    const descriptions = [...scope.querySelectorAll(roots)];
    if (scope.matches && scope.matches(roots)) descriptions.unshift(scope);
    descriptions.forEach(root => {
      if (root.dataset.rhlDescriptionLayout) return;
      root.dataset.rhlDescriptionLayout = '1';
      root.classList.add('rhl-description-layout');
      enhance(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  // Variant changes may replace the product-information subtree.
  const main = document.getElementById('MainContent');
  if (main) new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType === 1 && !node.closest('.rhl-spec-grid')) init(node);
    }));
  }).observe(main, { childList: true, subtree: true });
})();
